import { isEqual } from 'lodash-es';
import { CallRecordsFiltersType } from '../../hooks/use-phone-config-state-store';
import { defaultDateRangeMap } from '../../utils';
import { DEFAULT_NO_TIME_PERIOD } from '../filter-selectors/time-period-selector';

type RecentCallsFiltersBaseline = Omit<
  CallRecordsFiltersType,
  'status' | 'locationIds' | 'officePhoneNumbers' | 'aiOutcomes' | 'aiEvents' | 'handledByUserIds'
> & {
  status: ReadonlyArray<CallRecordsFiltersType['status'][number]>;
  locationIds: ReadonlyArray<string>;
  officePhoneNumbers?: ReadonlyArray<string>;
  aiOutcomes?: ReadonlyArray<NonNullable<CallRecordsFiltersType['aiOutcomes']>[number]>;
  aiEvents?: ReadonlyArray<string>;
  handledByUserIds?: ReadonlyArray<string>;
};

export const DEFAULT_RECENT_CALLS_FILTERS: RecentCallsFiltersBaseline = Object.freeze({
  status: Object.freeze([] as CallRecordsFiltersType['status']),
  phoneNumber: '',
  name: '',
  locationIds: Object.freeze([] as CallRecordsFiltersType['locationIds']),
  startDate: defaultDateRangeMap[DEFAULT_NO_TIME_PERIOD].startDate,
  endDate: defaultDateRangeMap[DEFAULT_NO_TIME_PERIOD].endDate,
  officePhoneNumbers: Object.freeze([] as NonNullable<CallRecordsFiltersType['officePhoneNumbers']>),
  aiOutcomes: Object.freeze([] as NonNullable<CallRecordsFiltersType['aiOutcomes']>),
  aiEvents: Object.freeze([] as NonNullable<CallRecordsFiltersType['aiEvents']>),
  callDirection: undefined,
  aiReceptionistHandled: undefined,
  handledByUserIds: Object.freeze([] as NonNullable<CallRecordsFiltersType['handledByUserIds']>),
  allOfficeUsers: undefined,
});

export const normalizeOfficePhoneNumbers = (officePhoneNumbers: unknown): string[] => {
  if (!Array.isArray(officePhoneNumbers)) {
    return [];
  }

  return officePhoneNumbers.filter(
    (phoneNumber): phoneNumber is string => typeof phoneNumber === 'string' && phoneNumber.length > 0
  );
};

export const createDefaultRecentCallsFilters = (
  overrides: Partial<CallRecordsFiltersType> = {}
): CallRecordsFiltersType => ({
  status: overrides.status ?? [],
  phoneNumber: overrides.phoneNumber ?? DEFAULT_RECENT_CALLS_FILTERS.phoneNumber,
  name: overrides.name ?? DEFAULT_RECENT_CALLS_FILTERS.name,
  locationIds: overrides.locationIds ?? [],
  startDate: overrides.startDate ?? DEFAULT_RECENT_CALLS_FILTERS.startDate,
  endDate: overrides.endDate ?? DEFAULT_RECENT_CALLS_FILTERS.endDate,
  officePhoneNumbers: normalizeOfficePhoneNumbers(overrides.officePhoneNumbers),
  aiOutcomes: overrides.aiOutcomes ?? [],
  aiEvents: overrides.aiEvents ?? [],
  callDirection: overrides.callDirection ?? DEFAULT_RECENT_CALLS_FILTERS.callDirection,
  aiReceptionistHandled: overrides.aiReceptionistHandled,
  handledByUserIds: overrides.handledByUserIds ?? [],
  allOfficeUsers: overrides.allOfficeUsers,
});

export const hasRecentCallsFiltersChanged = (
  incomingFilters: CallRecordsFiltersType,
  baselineFilters: CallRecordsFiltersType | RecentCallsFiltersBaseline = DEFAULT_RECENT_CALLS_FILTERS
) => !isEqual(incomingFilters, baselineFilters);
